package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.AccessGrantDto
import com.advaita.cloud.drive.shared.FileAccessResponse
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.PickerItemDto
import com.advaita.cloud.drive.shared.PickerResponse
import com.advaita.cloud.drive.shared.ShareDto
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.ShareApiException
import com.advaita.cloud.drive.ui.api.userFacingMessage
import com.advaita.cloud.drive.ui.components.ShareCap
import com.advaita.cloud.drive.ui.components.capForError
import com.advaita.cloud.drive.ui.components.capSentence
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch

/** The share routes the dialog calls. Injected, so jsTest can script them under Node. */
interface ShareBackend {
    suspend fun people(q: String): PickerResponse
    suspend fun recent(): PickerResponse
    suspend fun access(fileId: String): FileAccessResponse
    suspend fun share(fileId: String, principalKind: String, principalId: String, level: String): ShareDto
    suspend fun setLevel(grantId: String, level: String): ShareDto
    suspend fun revoke(grantId: String)
}

/** The real [ShareBackend]: DataHub's share routes. */
object HttpShareBackend : ShareBackend {
    override suspend fun people(q: String) = FilesApi.searchPeople(q)
    override suspend fun recent() = FilesApi.recentPeople()
    override suspend fun access(fileId: String) = FilesApi.fileAccess(fileId)
    override suspend fun share(fileId: String, principalKind: String, principalId: String, level: String) =
        FilesApi.createShare(fileId, principalKind, principalId, level)
    override suspend fun setLevel(grantId: String, level: String) = FilesApi.setShareLevel(grantId, level)
    override suspend fun revoke(grantId: String) = FilesApi.revokeShare(grantId)
}

/** A write (share, level, remove) throttled with a `429`. The search has its own sentence. */
const val WRITE_THROTTLED = "Too many changes in a short time. Wait a moment and try again."

/** Any other failure of a share call: DataHub or the vault did not complete it (`502` and the rest). */
const val SHARE_FAILED = "Sharing could not complete this."

/**
 * The text for a share failure that is NOT a cap: a race, the throttle, or a fault. A cap goes
 * through [capForError] and [capSentence] instead. [grantRoute] says the call named a grant, so
 * a `404` means the share is gone rather than the item.
 */
fun shareErrorMessage(t: Throwable, grantRoute: Boolean = false): String = when {
    t !is ShareApiException -> t.userFacingMessage()
    t.status == 429 -> WRITE_THROTTLED
    t.status == 409 && t.code == "conflict" -> "Someone changed this share at the same time. Try again."
    t.status == 422 && t.code == "unknown_team" -> "This team no longer exists."
    t.status == 422 && t.code == "grant_not_active" -> "This share has already ended."
    t.status == 404 && grantRoute -> "This share no longer exists."
    t.status == 404 -> "This item is no longer available."
    else -> SHARE_FAILED
}

/**
 * ADV-1330 F1/F2: the state of the share dialog for one file. DOM-free, like [SearchStore]. The
 * dialog component renders it and routes clicks and keys to it.
 *
 * [open] with a session cap ([com.advaita.cloud.drive.ui.components.capForMe]) shows only the
 * cap and calls nothing. Else it reads "Who has access", and on success the recents. A refused
 * read is a cap when [capForError] names it ([loadCap]), else one error line ([loadError]).
 * A re-read after a write that fails keeps the last list and says so in [refreshError].
 *
 * The level list comes from the route alone ([levels]). A share refusal that S8 names for this
 * pick (row 6, the person has no row; row 7, the level is above the sharer's own) caps the Share
 * control until the pick or the level changes ([pickCap]). Any other refusal of a write is one
 * sentence for the whole dialog ([noticeCap] or [noticeError]), and the controls stay live.
 *
 * Every async answer checks the open sequence, so an answer for a dialog that closed, or that
 * reopened on another file, changes nothing.
 */
class ShareDialogStore(
    private val backend: ShareBackend = HttpShareBackend,
    private val scope: CoroutineScope = MainScope(),
    /** Called after the caller leaves (her own grant is gone), with the Leave's toast text. */
    var onLeft: (FileDto, String) -> Unit = { _, _ -> },
    /** Asks the caller to confirm a Leave. The app routes it to its confirm dialog. */
    var confirmLeave: suspend (LeavePrompt) -> Boolean = { true },
    /** Shows a short confirmation, for example after Remove. */
    var notify: (String) -> Unit = {},
) {
    var target: FileDto? by mutableStateOf(null)
        private set
    val isOpen: Boolean get() = target != null

    var loading: Boolean by mutableStateOf(false)
        private set
    var access: FileAccessResponse? by mutableStateOf(null)
        private set
    var loadCap: ShareCap? by mutableStateOf(null)
        private set
    var loadError: String? by mutableStateOf(null)
        private set

    /** A re-read after a write failed. The last list stays on screen under this line. */
    var refreshError: String? by mutableStateOf(null)
        private set

    val picker = SharePickerStore(search = { backend.people(it) }, recent = { backend.recent() }, scope = scope)

    var level: String by mutableStateOf("")
        private set
    var pickCap: ShareCap? by mutableStateOf(null)
        private set
    var noticeCap: ShareCap? by mutableStateOf(null)
        private set
    var noticeError: String? by mutableStateOf(null)
        private set
    var busy: Boolean by mutableStateOf(false)
        private set

    /** F2: grant ids with a write in flight. Their controls are disabled until it lands. */
    var rowBusy: Set<String> by mutableStateOf(emptySet())
        private set

    /** F2: the caps that a refused write put on a row (S8 rows 7 and 8), by grant id. */
    var rowCaps: Map<String, ShareCap> by mutableStateOf(emptyMap())
        private set

    /**
     * F2: a count for each row, bumped when a write on it ends. The renderer keys the row's level
     * control on it, so a refused change rebuilds the control on the grant's real level.
     */
    var rowRevision: Map<String, Int> by mutableStateOf(emptyMap())
        private set

    private var openSeq = 0

    /** The levels the caller may give here, from the route alone (S6). */
    val levels: List<String> get() = access?.levels.orEmpty()

    /** The owner's own name for row 8, null when az2 did not give it (then "the owner"). */
    val ownerName: String? get() = access?.owner?.name?.takeIf { it.isNotBlank() }

    val pickCapSentence: String?
        get() = pickCap?.let { capSentence(it, owner = ownerName, person = picker.picked?.let(::pickerLabel)) }

    val canShare: Boolean
        get() = access != null && loadCap == null && !busy && picker.picked != null && pickCap == null && level in levels

    fun open(file: FileDto, sessionCap: ShareCap?) {
        close()
        target = file
        if (sessionCap != null) {
            loadCap = sessionCap
            return
        }
        val seq = openSeq
        scope.launch {
            if (readAccess(file, seq, keepOnFailure = false)) picker.open()
        }
    }

    fun close() {
        openSeq++
        target = null
        access = null
        loading = false
        loadCap = null
        loadError = null
        refreshError = null
        level = ""
        pickCap = null
        noticeCap = null
        noticeError = null
        busy = false
        rowBusy = emptySet()
        rowCaps = emptyMap()
        rowRevision = emptyMap()
        picker.hideFromRecents(emptySet())
        picker.close()
    }

    /** Chooses a picker row. False for a row that cannot be picked (Sam, S4). */
    fun choose(item: PickerItemDto): Boolean {
        val ok = picker.pick(item)
        if (ok) { pickCap = null; clearNotice() }
        return ok
    }

    /** Chooses the picker's keyboard row (Enter or Space in the list). */
    fun chooseActive(): Boolean = picker.items.getOrNull(picker.activeIndex)?.let(::choose) ?: false

    fun clearChoice() {
        picker.clearPick()
        pickCap = null
    }

    /** Chooses the level to give. A level the route did not offer is ignored. */
    fun chooseLevel(l: String) {
        if (l !in levels) return
        level = l
        if (pickCap == ShareCap.ABOVE_OWN_LEVEL) pickCap = null
    }

    /** Shares the file with the picked person or team at [level], then reads the list again. */
    fun share() {
        val file = target ?: return
        val pick = picker.picked ?: return
        if (!canShare) return
        val seq = openSeq
        busy = true
        clearNotice()
        scope.launch {
            try {
                backend.share(file.id, pick.kind, pick.id, level)
                // Said now, so a failed re-read below cannot hide that the share happened.
                notify("Shared with ${pickerLabel(pick)}.")
                if (seq != openSeq) return@launch
                // Review, ADV-1458: the new recipient leaves Recent now, before the list is read
                // again, and also when that re-read fails.
                picker.alsoHideFromRecents("${pick.kind}:${pick.id}")
                picker.open()
                readAccess(file, seq, keepOnFailure = true)
            } catch (e: CancellationException) {
                throw e
            } catch (e: RedirectingException) {
                throw e
            } catch (t: Throwable) {
                if (seq != openSeq) return@launch
                when (val cap = (t as? ShareApiException)?.let(::capForError)) {
                    ShareCap.NOT_SIGNED_IN, ShareCap.ABOVE_OWN_LEVEL -> pickCap = cap
                    null -> noticeError = shareErrorMessage(t)
                    else -> noticeCap = cap
                }
            } finally {
                if (seq == openSeq) busy = false
            }
        }
    }

    /** F2: the controls of one "Who has access" row, as the flags and any refused write drive them. */
    fun controlsFor(g: AccessGrantDto): AccessRowControls {
        val a = access
        return accessRowControls(
            g, levels, ownerName, a?.peopleStatus, a?.teamsStatus, rowCaps[g.grantId], itemName = target?.displayName,
        )
    }

    /** F2: changes [g] to [newLevel], when its row allows it, then reads the list again. */
    fun changeLevel(g: AccessGrantDto, newLevel: String) {
        if (newLevel == g.level || newLevel !in levels || !controlsFor(g).levelEnabled) {
            bumpRevision(g.grantId)
            return
        }
        rowWrite(g) {
            backend.setLevel(g.grantId, newLevel)
            true
        }
    }

    /**
     * F2: removes [g], or leaves it when it is the caller's own. Remove has no confirm step and
     * ends with a toast naming the person (the frontend build ruling, item 3). Leave asks first
     * (coordinator decision (c)). After Leave the caller may no longer reach the file, so this
     * dialog closes, if it is still the one that asked, and [onLeft] runs either way.
     */
    fun remove(g: AccessGrantDto) {
        val c = controlsFor(g)
        val action = c.removeAction ?: return
        val file = target ?: return
        if (action == RemoveAction.LEAVE) {
            val prompt = c.leave ?: leavePrompt(file.displayName)
            val seq = openSeq
            scope.launch {
                if (!confirmLeave(prompt)) return@launch
                if (seq != openSeq) return@launch
                rowWrite(g) {
                    backend.revoke(g.grantId)
                    if (seq == openSeq) close()
                    onLeft(file, prompt.done)
                    false
                }
            }
            return
        }
        val a = access
        val toast = removedToast(g.principalKind, g.principal, if (g.principalKind == "TEAM") a?.teamsStatus else a?.peopleStatus)
        rowWrite(g) {
            backend.revoke(g.grantId)
            notify(toast)
            true
        }
    }

    /** Runs one write on a row. [write] answers whether to read the list again afterwards. */
    private fun rowWrite(g: AccessGrantDto, write: suspend () -> Boolean) {
        val file = target ?: return
        if (g.grantId in rowBusy) return
        val seq = openSeq
        rowBusy = rowBusy + g.grantId
        rowCaps = rowCaps - g.grantId
        clearNotice()
        scope.launch {
            try {
                val reread = write()
                if (seq != openSeq) return@launch
                if (reread) readAccess(file, seq, keepOnFailure = true)
            } catch (e: CancellationException) {
                throw e
            } catch (e: RedirectingException) {
                throw e
            } catch (t: Throwable) {
                if (seq != openSeq) return@launch
                when (val cap = (t as? ShareApiException)?.let(::capForError)) {
                    ShareCap.ABOVE_OWN_LEVEL, ShareCap.NOT_ON_CHAIN -> rowCaps = rowCaps + (g.grantId to cap)
                    null -> {
                        noticeError = shareErrorMessage(t, grantRoute = true)
                        // The share is gone or has ended: the list on screen is stale.
                        if (t is ShareApiException && (t.status == 404 || t.code == "grant_not_active")) {
                            readAccess(file, seq, keepOnFailure = true)
                        }
                    }
                    else -> noticeCap = cap
                }
            } finally {
                if (seq == openSeq) {
                    rowBusy = rowBusy - g.grantId
                    bumpRevision(g.grantId)
                }
            }
        }
    }

    private fun bumpRevision(grantId: String) {
        rowRevision = rowRevision + (grantId to ((rowRevision[grantId] ?: 0) + 1))
    }

    internal fun clearNotice() {
        noticeCap = null
        noticeError = null
    }

    /**
     * Reads "Who has access" for [file]. True on success. On the first read a refusal sets
     * [loadCap] or [loadError] and there is no list. On a re-read after a write
     * ([keepOnFailure]), the last list stays and [refreshError] says the list may be stale,
     * unless the refusal is a cap, which replaces the list as on the first read.
     */
    private suspend fun readAccess(file: FileDto, seq: Int, keepOnFailure: Boolean): Boolean {
        loading = true
        try {
            val a = backend.access(file.id)
            if (seq != openSeq) return false
            access = a
            loadCap = null
            loadError = null
            refreshError = null
            picker.hideFromRecents(
                (a.grants.filter { it.principalKind == "USER" || it.principalKind == "TEAM" }
                    .mapNotNull { g -> g.principal.id?.let { "${g.principalKind}:$it" } } +
                    listOfNotNull(a.owner.id?.let { "USER:$it" })).toSet(),
            )
            if (level !in a.levels) level = a.levels.firstOrNull().orEmpty()
            return true
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            if (seq != openSeq) return false
            val cap = (t as? ShareApiException)?.let(::capForError)
            if (keepOnFailure && access != null && (cap == null || cap == ShareCap.OUTAGE)) {
                refreshError = "The list could not be refreshed. Close and open this dialog to see the latest."
                return false
            }
            access = null
            if (cap != null) loadCap = cap else loadError = shareErrorMessage(t)
            return false
        } finally {
            if (seq == openSeq) loading = false
        }
    }
}
